import classNames from 'classnames';
import { Tile } from 'common/tile/tile.js';
import { AdvertComponent } from 'components/advert/advert-component';
import { AdvertErrorBoundary } from 'components/advert/advert-error-boundary.js';
import { getMergedAdData } from 'components/advert/utils/ad-targeting-merge';
import { ScreenSizeContext } from 'page-context/column-layout/column-layout-context';
import { useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { AdConfig, IAdDefinitionProps } from 'redux-state/advertisement/types';
import { IModule } from 'redux-state/layout/types.js';
import { IRepoState } from 'redux-state/rootReducer';
import { isDevEnv } from 'utilities/consts/webpack-consts.js';
import { messageLog } from 'utilities/helpers/logger/message-logger.js';
import { modulePathIsPrimaryLeft, modulePathIsPrimaryRight } from 'utilities/modulePath/layout-modulepath.js';
import { getWindow } from 'utilities/window';
import { AdvertiseWithMetService } from './advertise-with-metservice';
import { ModulePath } from 'utilities/modulePath/modulePath';

export const AdvertModule = (props: IModule<IAdDefinitionProps & AdConfig>) => {
  const { config, modulePath } = props;
  const adSize = useContext(ScreenSizeContext);
  const { adDefinitions, targeting, account: ac } = useSelector((state: IRepoState) => state.ADVERTISEMENT);
  const [adPopulated, setAdPopulated] = useState(false);
  const adDisplayTrueCB = useCallback(() => {
    setAdPopulated(true);
  }, []);

  const adData = getMergedAdData({
    ad: config,
    defs: adDefinitions,
    adSize,
  });

  if (!adData) {
    if (isDevEnv(false)) {
      messageLog.info(`Module ad config is incorrect: ${config}`);
    }
    return null;
  }

  /** This is an ad slot in a static map left slot, if the map takeover is displaying then do not show */
  if (config.isStaticMapSlot && getWindow()?.AdUtils.getIsMapTakeoverDisplaying()) {
    messageLog.ads(`MapTakeoverSlot: Not displaying static map slot ${config.def} as map takeover is displaying`);
    return null;
  }

  const isLeft = modulePathIsPrimaryLeft(modulePath);
  const isRight = modulePathIsPrimaryRight(modulePath);
  const shouldBeCentred = [ModulePath.PRIMARY_MAIN, ModulePath.FOOTER_PROMO, ModulePath.SECONDARY_MAJOR].includes(modulePath);

  const classes = classNames('Tile--noShadow', {
    'u-textRight': isRight,
    'u-textLeft': isLeft,
    'u-sm-textCenter u-md-textCenter': isRight || isLeft || shouldBeCentred,
    'Tile--hide-empty': !adPopulated,
  });

  if (ac)
    return (
      <Tile dataModule="advert-mod" {...props} classes={classes}>
        <AdvertErrorBoundary>
          <AdvertComponent
            {...adData}
            targeting={targeting}
            ac={ac}
            origin={`module (${modulePath} ${config.id})`}
            adDisplayTrueCB={adDisplayTrueCB}
          />
        </AdvertErrorBoundary>
        {adData.showAdvertiseWithMS && <AdvertiseWithMetService />}
      </Tile>
    );

  return null;
};
